/* ═══════════════════════════════════════════════════════════════
   Dashboard — the project-dashboard HOST (Project Center R1, T4a).

   Owns: header (back / number / name / phase / status / Export /
   Customize), the widget grid + saved-views bar + layout persistence,
   the seven per-project data slices every widget reads through
   `ProjectWidgetCtx`, every ctx mutation, and takeover routing (the
   old Specs tab reborn as a full-bleed widget takeover). The widget
   catalog itself lives in ./widgets/registry.ts (T4b/T4c own the
   category barrels it assembles).

   Layout persistence, saved views, and the export/customize header
   controls are moved verbatim from the old combined Projects page
   (:174-318, :3352-3466, :3533+) — behavior unchanged, state renamed
   to drop the `editing`/`detailView` vocabulary of the old combined
   page.
   ═══════════════════════════════════════════════════════════════ */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronLeft, Download, MoreHorizontal, Lock } from 'lucide-react';
import {
  ContextMenu,
  ContextMenuTrigger,
  ContextMenuContent,
  ContextMenuItem,
} from '../../components/ui/context-menu';
import { Button } from '../../components/ui/button';
import { Icon } from '../../components/ui/Icon';
import { StatusBadge } from '../../components/StatusBadge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { WidgetGrid, type WidgetLayoutEntry } from '../../components/WidgetGrid';
import {
  visibleWidgets,
  defaultLayoutFor,
  applyFirmStarter,
} from '../../components/widgets/widgetVisibility';
import {
  migrateDashboardLayout,
  ensureRequiredWidgets,
} from '../../services/dashboardLayoutMigrations';
import { DashboardViewService } from '../../services/dashboardViewService';
import { CustomWidgetService } from '../../services/customWidgetService';
import { FirmPrefs } from '../../services/firmPrefsService';
import { hasPermission } from '../../../shared/permissions';
import { isWeb } from '../../lib/platform';
import { TeamService } from '../../services/teamService';
import { TimeService } from '../../services/timeService';
import { ProjectService } from '../../services/projectService';
import { AdminService } from '../../services/adminService';
import { ProjectSpecService } from '../../services/projectSpecService';
import { FormaService } from '../../services/formaService';
import {
  ProjectDesignBriefService,
  DesignBriefFieldService,
} from '../../services/projectDesignBriefService';
import {
  saveProject as saveProjectMutation,
  setCurrentPhase as setCurrentPhaseMutation,
} from './projectMutations';
import { sanitizeProjectName } from './helpers';
import { projectWidgets } from './widgets/registry';
import { getWidgetSource } from '../../lib/widgetSources';
import { customWidgetDef } from './widgets/custom-widget';
import { CustomWidgetBuilder } from './CustomWidgetBuilder';
import { DashboardViewsBar } from './DashboardViewsBar';
import { ConfirmDialog } from '../../components/ui/confirm-dialog';
import { usePref } from '../../lib/usePref';
import { pageGate } from '../../lib/pageGates';
import { ProjectToolHost, type ProjectToolKind } from './ProjectToolHost';
import {
  DEFAULT_PROJECT_PANEL_PREFS,
  type PanelSectionId,
  type ProjectPanelPrefs,
} from './projectPanelPrefs';
import type {
  Project,
  ProjectAssignment,
  TeamMember,
  BillingRole,
  ProjectPhase,
  Deliverable,
  Team,
  TeamAssignment,
  Organization,
  ProjectType,
  BillingType,
  ProjectScope,
  ProjectScopeAssignment,
  ProjectSpec,
  ProjectDesignBrief,
  DesignBriefField,
} from '../../../shared/types';
import type { Department, DashboardView, CustomWidget } from '../../../shared/entities';
import type { AuthUser } from '../../services/authService';
import type { DeepLink } from '../../types/navigation';
import { deriveCan } from './widgets/context';
import type {
  DashboardFocus,
  DashboardDrill,
  Forma,
  FirmPhaseDef,
  ProjectWidgetCtx,
  ProjectWidgetDef,
  SliceState,
} from './widgets/context';

export interface DashboardProps {
  project: Project;
  focus: DashboardFocus | null;
  onBack: () => void;
  authUser?: AuthUser | null;
  toast: (msg: string, isError?: boolean) => void;
  navigate?: (link: DeepLink) => void;
  onOpenProjectSpec?: (specId: string, projectId: string, opts?: { review?: boolean }) => void;
  onOpenForma?: (formaId: string, opts?: { review?: boolean }) => void;
  /** Tells the host (index.tsx) a mutation changed the project record
   *  (save, phase cycle, PIC/PM sync) so its `projects` list — and the
   *  `project` prop this component re-renders with next tick — stays
   *  current. Added in T4a; index.tsx's ProjectsPage implements it as
   *  a `setProjects` map-replace. */
  onProjectChange: (p: Project) => void;
  // Firm-wide reference data — already loaded once by index.tsx's
  // loadData; threaded straight through so the dashboard never
  // duplicates those fetches.
  orgs: Organization[];
  projectTypes: ProjectType[];
  billingTypes: BillingType[];
  firmPhases: FirmPhaseDef[];
  allScopes: ProjectScope[];
  members: TeamMember[];
  teams: Team[];
  teamAssignments: TeamAssignment[];
  departments: Department[];
}

const DASHBOARD_SURFACE = 'project_dashboard';

// Project Canvas R1 (T3b) — when a nested-tool drill's page gate fails, it
// falls back to today's in-page scroll instead of opening to a refusal.
const TOOL_FALLBACK_WIDGET: Record<ProjectToolKind, string> = {
  tasks: 'tasks',
  invoices: 'invoices',
  forecasting: 'forecast_outlook',
  'time-expense': 'budget',
};

/* ─── One independent, reloadable data source (Promise.allSettled
   discipline — a slow/failed source degrades to its own error state
   instead of blanking the whole dashboard). `key` must be a stable
   literal so the returned `reload` callback keeps one identity for
   the life of the component (see the two effects below that depend
   on it). ─── */
function useProjectSlice<T>(key: string, loader: () => Promise<T>, fallback: T): SliceState<T> {
  const [data, setData] = useState<T>(fallback);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const [error, setError] = useState<string | undefined>();
  const loaderRef = React.useRef(loader);
  loaderRef.current = loader;

  const reload = useCallback(async () => {
    setStatus('loading');
    try {
      const d = await loaderRef.current();
      setData(d);
      setStatus('ready');
      setError(undefined);
    } catch (err: any) {
      console.error(`[project dashboard] ${key} slice failed:`, err);
      setStatus('error');
      setError(err?.message || 'Failed to load');
    }
  }, [key]);

  return { data, status, error, reload };
}

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    !!window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

export function Dashboard({
  project,
  focus,
  onBack,
  authUser,
  toast,
  navigate,
  onOpenProjectSpec,
  onOpenForma,
  onProjectChange,
  orgs,
  projectTypes,
  billingTypes,
  firmPhases,
  allScopes,
  members,
  teams,
  teamAssignments,
  departments,
}: DashboardProps) {
  const perms = authUser?.permissions;
  const userPermissions = perms || [];
  // Closed = fully locked (Josh 2026-09-28). Every mutation `can.*` bool
  // below is ANDed with `!locked` in ONE place (deriveCan) — a widget
  // never re-derives its own lock check.
  const locked = project.status === 'closed';
  // Pure derivation lives in widgets/context.ts (deriveCan) so it is
  // importable/unit-testable outside this page — T3b permission audit.
  const can = useMemo(() => deriveCan(perms, locked), [perms, locked]);

  // Project Canvas R1, T2 — Details/Phase/Design brief retired their own
  // collapse/reorder chrome (ProjectPanel.tsx deleted); the one surviving
  // value from that UI is the user's saved SECTION ORDER (Decision 12),
  // carried into `ensureProjectDetailWidgets` below so a migrated layout
  // backfills the three stock widgets in the order the user had them.
  const [panelPrefs] = usePref<ProjectPanelPrefs>(
    'projectPanel',
    'projectPanel',
    DEFAULT_PROJECT_PANEL_PREFS
  );
  const sectionOrder: PanelSectionId[] =
    panelPrefs.order && panelPrefs.order.length === 3
      ? panelPrefs.order
      : DEFAULT_PROJECT_PANEL_PREFS.order;

  // Firm-wide reference list not yet loaded by index.tsx's loadData —
  // small standalone fetch, same pattern the old combined page used.
  const [billingRoles, setBillingRoles] = useState<BillingRole[]>([]);
  useEffect(() => {
    AdminService.billingRoles
      .list()
      .then(setBillingRoles)
      .catch(() => setBillingRoles([]));
  }, []);

  // ─── Per-project slices ───
  const assignments = useProjectSlice<ProjectAssignment[]>(
    'assignments',
    () => TeamService.listAssignments(undefined, project.id),
    []
  );
  const phases = useProjectSlice<ProjectPhase[]>(
    'phases',
    () => TimeService.syncPhasesWithDefaults(project.id),
    []
  );
  const deliverables = useProjectSlice<Deliverable[]>(
    'deliverables',
    () => TimeService.listDeliverables(project.id),
    []
  );
  const scopeAssignments = useProjectSlice<ProjectScopeAssignment[]>(
    'scopeAssignments',
    () => AdminService.projectScopeAssignments.list(project.id),
    []
  );
  const projectSpecs = useProjectSlice<ProjectSpec[]>(
    'projectSpecs',
    () => ProjectSpecService.listByProject(project.id),
    []
  );
  const formas = useProjectSlice<Forma[]>(
    'formas',
    async () => {
      const all = await FormaService.list();
      return all.filter((f) => f.projectId === project.id && !f.specId);
    },
    []
  );
  const designBrief = useProjectSlice<ProjectDesignBrief | null>(
    'designBrief',
    () => ProjectDesignBriefService.getByProject(project.id),
    null
  );
  const designBriefFields = useProjectSlice<DesignBriefField[]>(
    'designBriefFields',
    () => DesignBriefFieldService.listForProject(project.id),
    []
  );

  // Re-fetches the project record itself the SAME way it was loaded
  // initially (index.tsx's loadData → ProjectService.list()) — a bare
  // `list()` reads the already-updated in-memory cloudCache write-through,
  // so this is cheap, not a network round trip. Without this, a phase/
  // status/PIC write only updated `ctx.project` via the mutation's own
  // `onProjectChange` call; another tab's write (or a slower listener
  // order) never reached the header pill / Phase stepper (T3b — "phase in
  // two places" gap, docs/PROJECT_DATA_PLAN.md).
  const reloadProject = useCallback(async () => {
    try {
      const all = await ProjectService.list();
      const fresh = all.find((p) => p.id === project.id);
      if (fresh) onProjectChange(fresh);
    } catch (err) {
      console.error('[project dashboard] project reload failed:', err);
    }
  }, [project.id, onProjectChange]);

  const reloadAll = useCallback(() => {
    void Promise.allSettled([
      reloadProject(),
      assignments.reload(),
      phases.reload(),
      deliverables.reload(),
      scopeAssignments.reload(),
      projectSpecs.reload(),
      formas.reload(),
      designBrief.reload(),
      designBriefFields.reload(),
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    reloadProject,
    assignments.reload,
    phases.reload,
    deliverables.reload,
    scopeAssignments.reload,
    projectSpecs.reload,
    formas.reload,
    designBrief.reload,
    designBriefFields.reload,
  ]);

  useEffect(() => {
    reloadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.id]);

  useEffect(() => {
    window.addEventListener('specforma-sync-complete', reloadAll);
    return () => window.removeEventListener('specforma-sync-complete', reloadAll);
  }, [reloadAll]);

  // ─── Mutations reject on failure (a widget shows its own error);
  // the host additionally toasts the failure in plain words. ───
  async function withToast<T>(fn: () => Promise<T>): Promise<T> {
    try {
      return await fn();
    } catch (err: any) {
      toast(err?.message || 'Something went wrong', true);
      throw err;
    }
  }

  const saveProject = (p: Project) =>
    withToast(async () => {
      const { project: saved } = await saveProjectMutation(p, firmPhases);
      onProjectChange(saved);
      await Promise.allSettled([assignments.reload(), phases.reload()]);
      return saved;
    });

  // Close / Reopen (T3b) — the only write allowed on an already-closed
  // project. `withToast` (inside `saveProject`) already surfaces a failure;
  // this only adds the success toast + closes the confirm dialog.
  const closeOrReopenProject = async (action: 'close' | 'reopen') => {
    setArchiveBusy(true);
    try {
      await saveProject({ ...project, status: action === 'close' ? 'closed' : 'active' });
      toast(action === 'close' ? 'Project closed' : 'Project reopened');
      setConfirmArchive(null);
    } catch {
      /* withToast already toasted the failure */
    } finally {
      setArchiveBusy(false);
    }
  };

  const saveDeliverable = (d: Deliverable) =>
    withToast(async () => {
      await TimeService.saveDeliverable(d);
      await deliverables.reload();
    });

  const deleteDeliverable = (id: string) =>
    withToast(async () => {
      await TimeService.deleteDeliverable(id);
      await deliverables.reload();
    });

  const setCurrentPhase = (phaseId: string) =>
    withToast(async () => {
      const result = await setCurrentPhaseMutation(project, phaseId);
      onProjectChange(result.project);
      await phases.reload();
    });

  const savePhase = (p: ProjectPhase) =>
    withToast(async () => {
      await TimeService.savePhase(p);
      await phases.reload();
    });

  // Deleting the CURRENT phase is refused server-side (409) — the Phase
  // widget/PhasePanel disable the control for that phase, title "Set
  // another phase first", so this only runs for a non-current phase in
  // normal use. A stale-state attempt on the current phase surfaces the
  // server's 409 message via `withToast` → `ctx.toast(message, true)`.
  const deletePhase = (id: string) =>
    withToast(async () => {
      await TimeService.deletePhase(id);
      if (project.currentPhaseId === id) {
        const { project: saved } = await saveProjectMutation(
          { ...project, currentPhaseId: null },
          firmPhases
        );
        onProjectChange(saved);
      }
      await phases.reload();
    });

  const reorderPhases = (orderedIds: string[]) =>
    withToast(async () => {
      const byId = new Map(phases.data.map((p) => [p.id, p]));
      const results = await Promise.allSettled(
        orderedIds.map((id, i) => {
          const p = byId.get(id);
          if (!p || p.sortOrder === i) return Promise.resolve();
          return TimeService.savePhase({ ...p, sortOrder: i });
        })
      );
      await phases.reload();
      const failed = results.find((r) => r.status === 'rejected');
      if (failed) throw new Error('Some phases failed to reorder');
    });

  const assignMember = (memberId: string, role: string) =>
    withToast(async () => {
      if (assignments.data.some((a) => a.teamMemberId === memberId)) {
        throw new Error('Member already assigned to this project');
      }
      await TeamService.assign(project.id, memberId, role);
      await assignments.reload();
      toast('Member assigned');
    });

  const assignTeam = (teamId: string) =>
    withToast(async () => {
      const tas = await TeamService.listTeamAssignments(teamId);
      const existingIds = new Set(assignments.data.map((a) => a.teamMemberId));
      let added = 0;
      for (const ta of tas) {
        if (!existingIds.has(ta.teamMemberId)) {
          await TeamService.assign(project.id, ta.teamMemberId, 'member');
          added++;
        }
      }
      await assignments.reload();
      const team = teams.find((t) => t.id === teamId);
      toast(`Assigned ${added} member${added !== 1 ? 's' : ''} from ${team?.name || 'team'}`);
    });

  const setAssignmentRole = (assignmentId: string, role: string) =>
    withToast(async () => {
      const a = assignments.data.find((x) => x.id === assignmentId);
      if (!a) return;
      if (role === 'pic' || role === 'pm') {
        // PIC/PM live on the project record — the record is the source.
        const field: 'picId' | 'pmId' = role === 'pic' ? 'picId' : 'pmId';
        if (project[field] !== a.teamMemberId) {
          const updatedProject = await ProjectService.save({ ...project, [field]: a.teamMemberId });
          onProjectChange(updatedProject);
        }
      } else {
        await TeamService.assign(a.projectId, a.teamMemberId, role);
        await TeamService.unassign(assignmentId);
        if (role === 'member') {
          let updated = project;
          let changed = false;
          if (project.picId === a.teamMemberId) {
            updated = { ...updated, picId: undefined };
            changed = true;
          }
          if (project.pmId === a.teamMemberId) {
            updated = { ...updated, pmId: undefined };
            changed = true;
          }
          if (changed) {
            updated = await ProjectService.save(updated);
            onProjectChange(updated);
          }
        }
      }
      await assignments.reload();
    });

  const removeAssignment = (assignmentId: string) =>
    withToast(async () => {
      const a = assignments.data.find((x) => x.id === assignmentId);
      await TeamService.unassign(assignmentId);
      // A removed member can't stay PIC/PM — clear it on the record.
      if (a && (project.picId === a.teamMemberId || project.pmId === a.teamMemberId)) {
        const updated = await ProjectService.save({
          ...project,
          picId: project.picId === a.teamMemberId ? undefined : project.picId,
          pmId: project.pmId === a.teamMemberId ? undefined : project.pmId,
        });
        onProjectChange(updated);
      }
      await assignments.reload();
      toast('Member removed');
    });

  const saveDesignBrief = (b: ProjectDesignBrief) =>
    withToast(async () => {
      await ProjectDesignBriefService.save(b);
      await designBrief.reload();
    });

  const saveDesignBriefField = (f: DesignBriefField) =>
    withToast(async () => {
      const saved = await DesignBriefFieldService.save(f);
      await designBriefFields.reload();
      return saved;
    });

  const removeDesignBriefField = (id: string) =>
    withToast(async () => {
      await DesignBriefFieldService.remove(id);
      await designBriefFields.reload();
    });

  const reorderDesignBriefFields = (orderedIds: string[]) =>
    withToast(async () => {
      await DesignBriefFieldService.reorder(project.id, orderedIds);
      await designBriefFields.reload();
    });

  // ─── User-built widgets (Project Center widget builder, T5-UI) ───
  const [customWidgets, setCustomWidgets] = useState<CustomWidget[]>([]);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editingWidget, setEditingWidget] = useState<CustomWidget | null>(null);
  const [deletingWidget, setDeletingWidget] = useState<CustomWidget | null>(null);
  const [deletingWidgetBusy, setDeletingWidgetBusy] = useState(false);

  const reloadCustomWidgets = useCallback(() => {
    CustomWidgetService.list(DASHBOARD_SURFACE)
      .then(setCustomWidgets)
      .catch(() => setCustomWidgets([]));
  }, []);
  useEffect(() => {
    reloadCustomWidgets();
    window.addEventListener('specforma-sync-complete', reloadCustomWidgets);
    return () => window.removeEventListener('specforma-sync-complete', reloadCustomWidgets);
  }, [reloadCustomWidgets]);

  // Appended to the registry list BEFORE visibleWidgets runs anywhere below,
  // so permissions filter a user-built widget exactly like a stock one — a
  // shared widget the viewer lacks a key for is simply absent.
  const customDefs = useMemo(
    () =>
      customWidgets
        .map((w) => {
          const source = getWidgetSource(w.definition.sourceId);
          if (!source) return null;
          return customWidgetDef(w, source, {
            onEdit: (row) => {
              setEditingWidget(row);
              setBuilderOpen(true);
            },
            onDuplicate: (row) => void duplicateCustomWidget(row),
            onRename: (row, name) => void renameCustomWidget(row, name),
            onDelete: (row) => setDeletingWidget(row),
          });
        })
        .filter((d): d is ProjectWidgetDef => !!d),
    [customWidgets]
  );
  const allProjectWidgets = useMemo(() => [...projectWidgets, ...customDefs], [customDefs]);

  const deleteCustomWidget = async () => {
    if (!deletingWidget) return;
    setDeletingWidgetBusy(true);
    try {
      await CustomWidgetService.delete(deletingWidget.id);
      setDeletingWidget(null);
      reloadCustomWidgets();
      toast('Widget deleted');
    } catch (err: any) {
      toast(err?.message || 'Could not delete widget', true);
    } finally {
      setDeletingWidgetBusy(false);
    }
  };

  // Builder R2 — catalog "…" menu + placed-widget header menu share these
  // two (Duplicate/Rename never open the full editor Sheet).
  const duplicateCustomWidget = async (row: CustomWidget) => {
    try {
      const memberId = authUser?.teamMemberId || authUser?.id || '';
      await CustomWidgetService.save({
        id: '',
        ownerId: memberId,
        surface: row.surface,
        name: `${row.name} (copy)`,
        definition: row.definition,
        shared: false,
      });
      reloadCustomWidgets();
      toast('Widget duplicated');
    } catch (err: any) {
      toast(err?.message || 'Could not duplicate widget', true);
    }
  };

  const renameCustomWidget = async (row: CustomWidget, name: string) => {
    try {
      await CustomWidgetService.save({ ...row, name });
      reloadCustomWidgets();
    } catch (err: any) {
      toast(err?.message || 'Could not rename widget', true);
    }
  };

  // Final gate (mirrors server.js custom_widgets update/delete exactly —
  // same expression custom-widget.tsx's header menu uses) — governs the
  // catalog's "…" menu on "Your widgets" / "Firm widgets" rows.
  const canManageCustomWidget = useCallback(
    (ownerId: string, shared: boolean) => {
      if (locked) return false;
      const memberId = authUser?.teamMemberId || authUser?.id;
      const isOwner = !!memberId && memberId === ownerId;
      const ownerManage =
        isOwner &&
        (hasPermission(userPermissions, 'dashboards.edit_own') ||
          hasPermission(userPermissions, 'dashboards.edit_any') ||
          hasPermission(userPermissions, 'dashboards.delete'));
      return ownerManage || (shared && hasPermission(userPermissions, 'dashboards.share_views'));
    },
    [locked, authUser?.teamMemberId, authUser?.id, userPermissions]
  );

  const editCustomWidgetById = (id: string) => {
    const row = customWidgets.find((w) => w.id === id);
    if (row) {
      setEditingWidget(row);
      setBuilderOpen(true);
    }
  };
  const duplicateCustomWidgetById = (id: string) => {
    const row = customWidgets.find((w) => w.id === id);
    if (row) void duplicateCustomWidget(row);
  };
  const renameCustomWidgetById = (id: string, name: string) => {
    const row = customWidgets.find((w) => w.id === id);
    if (row) void renameCustomWidget(row, name);
  };
  const deleteCustomWidgetById = (id: string) => {
    const row = customWidgets.find((w) => w.id === id);
    if (row) setDeletingWidget(row);
  };

  // ─── Takeover routing ───
  // A takeover must never open for a widget the user's permissions hide —
  // resolve against the permission-filtered set, not the raw catalog.
  const visibleProjectWidgets = useMemo(
    () => visibleWidgets(allProjectWidgets, userPermissions),
    [allProjectWidgets, userPermissions]
  );
  // Project Canvas R1 (T3b, Decision 5/6): the takeover area hosts TWO
  // kinds now — a widget's own full-bleed takeover (Specs, Deliverables;
  // unchanged chrome) or a nested REAL sidebar page (Tasks/Billing/
  // Forecasting/Time & Expense), scoped to this project via `ProjectToolHost`.
  // Generalizing `takeoverId: string | null` into this union is the ONE
  // host both routes converge on (Finding 3) instead of two parallel embeds.
  type TakeoverState =
    | { kind: 'widget'; widgetId: string }
    | { kind: 'tool'; tool: ProjectToolKind; deepLink?: DeepLink };
  const [takeover, setTakeover] = useState<TakeoverState | null>(null);
  const activeWidgetTakeoverDef =
    takeover?.kind === 'widget'
      ? visibleProjectWidgets.find((w) => w.id === takeover.widgetId)
      : undefined;

  // Decision 6 (T3b item 6): Back restores the dashboard exactly — the
  // grid's scroll offset and focus are captured right before a takeover
  // opens, and restored once the grid remounts (the effect below fires
  // after `takeover` goes back to null and the grid div is back in the DOM).
  const gridScrollRef = React.useRef(0);
  const returnFocusRef = React.useRef<HTMLElement | null>(null);
  const captureBeforeTakeover = useCallback(() => {
    const grid = document.getElementById('project-dashboard-grid');
    gridScrollRef.current = grid ? grid.scrollTop : 0;
    returnFocusRef.current = (document.activeElement as HTMLElement) || null;
  }, []);
  const openWidgetTakeover = useCallback(
    (widgetId: string) => {
      captureBeforeTakeover();
      setTakeover({ kind: 'widget', widgetId });
    },
    [captureBeforeTakeover]
  );
  const closeTakeoverState = useCallback(() => setTakeover(null), []);
  useEffect(() => {
    if (takeover) return;
    const grid = document.getElementById('project-dashboard-grid');
    returnFocusRef.current?.focus?.();
    returnFocusRef.current = null;
    if (!grid) return;
    const target = gridScrollRef.current;
    if (target <= 0) return;
    // Applying scrollTop the instant the grid remounts clamps to a too-short
    // scrollHeight — the auto-height widget bodies above the fold haven't
    // regrown yet. Keep re-applying until the grid is tall enough to hold
    // the saved offset (first frame covers short dashboards — set once,
    // stop), bail the moment the user touches the grid themselves, and give
    // up after a short budget so this never runs forever.
    let done = false;
    let ro: ResizeObserver | null = null;
    let budgetTimer: number | null = null;
    const CANCEL_EVENTS = ['wheel', 'touchstart', 'pointerdown', 'keydown'] as const;
    const finish = () => {
      if (done) return;
      done = true;
      ro?.disconnect();
      if (budgetTimer !== null) window.clearTimeout(budgetTimer);
      for (const ev of CANCEL_EVENTS) grid.removeEventListener(ev, onUserInteract);
    };
    const onUserInteract = () => finish();
    const tryApply = () => {
      if (done) return;
      if (grid.scrollHeight - grid.clientHeight >= target) {
        grid.scrollTop = target;
        finish();
      }
    };
    for (const ev of CANCEL_EVENTS) {
      grid.addEventListener(ev, onUserInteract, { passive: true, once: true });
    }
    tryApply();
    if (!done) {
      // `grid` itself (the overflow-auto scroller) never changes size as
      // its content grows — only its scrollHeight does, which
      // ResizeObserver cannot see. Observe the grid's content wrapper
      // (WidgetGrid's own root, the thing that actually gets taller as
      // auto-height widget bodies re-measure after remount) instead;
      // fall back to the grid if that wrapper isn't there.
      const content = (grid.firstElementChild as HTMLElement | null) || grid;
      ro = new ResizeObserver(tryApply);
      ro.observe(content);
      budgetTimer = window.setTimeout(finish, 1500);
    }
    return finish;
  }, [takeover]);

  const scrollToWidget = useCallback((id: string) => {
    // Every id is a widget-grid tile (`[data-widget-id]`) — Project Canvas
    // R1 turned Details/Phase/Design brief back into real widgets
    // (`project_details`/`project_phase`/`project_brief`), so there is no
    // more special-cased non-widget element id to fall back to.
    const el = (document.querySelector(`[data-widget-id="${id}"]`) ||
      document.getElementById(id)) as HTMLElement | null;
    if (!el) return;
    el.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
    const prev = el.style.boxShadow;
    const prevTransition = el.style.transition;
    el.style.transition = prefersReducedMotion()
      ? 'none'
      : 'box-shadow var(--duration-slow, 300ms) ease';
    el.style.boxShadow = '0 0 0 2px rgba(var(--acr), 0.55)';
    window.setTimeout(() => {
      el.style.boxShadow = prev;
      el.style.transition = prevTransition;
    }, 1200);
  }, []);

  // Decision 5/6/13 (T3b item 4/5): opens a nested REAL sidebar page
  // scoped to this project, gated by that page's OWN gate (`pageGate` —
  // the same predicate the sidebar nav filter uses, so the two can never
  // drift). A failed gate never opens to a refusal — it falls back to
  // today's in-page scroll, and the widget itself hides its expand control
  // (tasks/invoices/forecast-outlook widgets check `pageGate` directly).
  const openNestedTool = useCallback(
    (tool: ProjectToolKind, deepLink: DeepLink) => {
      if (!pageGate(tool, userPermissions)) {
        scrollToWidget(TOOL_FALLBACK_WIDGET[tool]);
        return;
      }
      captureBeforeTakeover();
      // Decision 7: the nested filter is PRESET on the deep link, never
      // whatever the sidebar page's own remembered filters hold — force
      // projectId to THIS project regardless of what the caller passed.
      setTakeover({
        kind: 'tool',
        tool,
        deepLink: { ...deepLink, projectId: project.id } as DeepLink,
      });
    },
    [userPermissions, scrollToWidget, captureBeforeTakeover, project.id]
  );
  // Wraps the raw `navigate` prop for every widget (incl. custom-widget row
  // drills, which call `ctx.navigate` directly for plain DeepLinks —
  // `custom-widget.tsx`'s `fireDrill`): a DeepLink whose view is one of the
  // four nested tools opens it in-project; every other view (e.g. `team`)
  // passes through to the real cross-page `navigate` unchanged.
  const wrappedNavigate = useCallback(
    (link: DeepLink) => {
      if (
        link.view === 'tasks' ||
        link.view === 'invoices' ||
        link.view === 'forecasting' ||
        link.view === 'time-expense'
      ) {
        openNestedTool(link.view, link);
        return;
      }
      navigate?.(link);
    },
    [openNestedTool, navigate]
  );

  const handleDrill = useCallback(
    (target: DashboardDrill) => {
      switch (target.kind) {
        case 'tab':
          if (target.tab === 'specs') openWidgetTakeover('specs');
          else closeTakeoverState();
          return;
        case 'widget-focus':
          scrollToWidget(target.widgetId);
          return;
        case 'budget-editor':
          scrollToWidget('budget');
          return;
        case 'phase':
          scrollToWidget('project_phase');
          return;
        case 'deliverable':
          scrollToWidget('deliverables');
          return;
        case 'task':
          // Decision 5/6/C: was a scroll-only dead end (Finding 2) — nests
          // the real Tasks page with this task's existing detail open.
          openNestedTool('tasks', { view: 'tasks', projectId: project.id, taskId: target.taskId });
          return;
        case 'new-task':
          openNestedTool('tasks', { view: 'tasks', projectId: project.id, create: true });
          return;
        case 'time-entry-list':
          // Decision 14: nests Time & Expense (not rebuilt here) IF its
          // own page gate passes; falls back to the in-page scroll.
          openNestedTool('time-expense', {
            view: 'time-expense',
            tab: 'timesheets',
            projectId: project.id,
            weekStart: target.weekStart,
          });
          return;
        case 'forecast-week':
          openNestedTool('forecasting', {
            view: 'forecasting',
            projectId: project.id,
            weekStart: target.weekStart,
          });
          return;
        case 'invoice':
          openNestedTool('invoices', {
            view: 'invoices',
            projectId: project.id,
            invoiceId: target.invoiceId,
          });
          return;
        case 'invoice-list':
          openNestedTool('invoices', {
            view: 'invoices',
            projectId: project.id,
            status: target.status || 'all',
          });
          return;
      }
    },
    [project.id, scrollToWidget, openNestedTool, openWidgetTakeover, closeTakeoverState]
  );

  // Honor `focus` on mount and whenever it changes.
  useEffect(() => {
    if (!focus) return undefined;
    if (focus.kind === 'takeover') {
      // A hidden/permission-gated widget id falls back to the plain
      // dashboard silently rather than opening a takeover the user
      // shouldn't see.
      if (visibleProjectWidgets.some((w) => w.id === focus.widgetId)) {
        openWidgetTakeover(focus.widgetId);
      }
      return undefined;
    }
    // Give the grid a tick to mount before querying the DOM.
    const t = window.setTimeout(() => scrollToWidget(focus.widgetId), 60);
    return () => window.clearTimeout(t);
  }, [focus, scrollToWidget, visibleProjectWidgets, openWidgetTakeover]);

  // ─── Saved views + layout persistence (moved verbatim from the old
  // combined Projects page, :174-318) ───
  const [dashboardViews, setDashboardViews] = useState<DashboardView[]>([]);
  const [activeViewId, setActiveViewId] = useState<string | null>(null);
  const [dashboardLayout, setDashboardLayoutState] = useState<WidgetLayoutEntry[]>([]);
  const [customizing, setCustomizing] = useState(false);
  const [showSaveViewDialog, setShowSaveViewDialog] = useState(false);
  // Close / Reopen project (T3b) — one shared confirm, gated `projects.archive`.
  const [confirmArchive, setConfirmArchive] = useState<'close' | 'reopen' | null>(null);
  const [archiveBusy, setArchiveBusy] = useState(false);
  const [saveViewName, setSaveViewName] = useState('');
  const [saveViewShared, setSaveViewShared] = useState(false);

  const reloadDashboardViews = useCallback(async () => {
    const userId = authUser?.teamMemberId || authUser?.id;
    if (!userId) return;
    try {
      const all = await DashboardViewService.listForSurface(DASHBOARD_SURFACE, userId);
      setDashboardViews(all);
    } catch (err) {
      console.error('[dashboard views load]', err);
    }
  }, [authUser?.teamMemberId, authUser?.id]);

  // Fallback layout, priority order:
  //   1. legacy team_members.dashboardLayouts.project_detail blob.
  //   2. the firm starter (FirmPrefs), filtered through what THIS viewer's
  //      permissions allow — an admin-saved starter can never hand out a
  //      widget the viewer lacks the key for.
  //   3. else empty — WidgetGrid's `defaultLayout` prop (registry order) wins.
  const resolveFallbackLayout = useCallback(async (): Promise<WidgetLayoutEntry[]> => {
    const memberId = authUser?.teamMemberId || authUser?.id;
    if (!memberId) return [];
    try {
      const allMembers = await TeamService.list();
      const me = allMembers.find((m: any) => m.id === memberId);
      const saved = (me as any)?.dashboardLayouts?.project_detail;
      if (Array.isArray(saved) && saved.length > 0) return saved;
    } catch {
      /* fall through to the firm starter */
    }
    // The starter runs through the same migrations as a saved layout (a
    // starter saved before Attention existed still yields it), THEN the
    // viewer's permission filter.
    return applyFirmStarter(
      migrateDashboardLayout(
        (FirmPrefs.get(`dashboard.starter.${DASHBOARD_SURFACE}`) as
          | WidgetLayoutEntry[]
          | undefined) ?? []
      ),
      visibleWidgets(allProjectWidgets, userPermissions)
    );
  }, [authUser?.teamMemberId, authUser?.id, allProjectWidgets, userPermissions]);

  // Load the user's saved layout once per session. Priority: a default
  // DashboardView, if any, else resolveFallbackLayout above.
  useEffect(() => {
    const memberId = authUser?.teamMemberId || authUser?.id;
    if (!memberId) return;
    (async () => {
      try {
        await reloadDashboardViews();
        const def = await DashboardViewService.getDefault(DASHBOARD_SURFACE, memberId);
        if (def && Array.isArray((def.config as any)?.layout)) {
          setDashboardLayoutState((def.config as any).layout);
          setActiveViewId(def.id);
          return;
        }
        setDashboardLayoutState(await resolveFallbackLayout());
      } catch {
        /* ignore — user gets the default layout */
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser?.teamMemberId, authUser?.id]);

  const saveDashboardLayout = async (next: WidgetLayoutEntry[]) => {
    setDashboardLayoutState(next);
    const memberId = authUser?.teamMemberId || authUser?.id;
    if (!memberId) return;
    try {
      if (activeViewId) {
        const v = dashboardViews.find((x) => x.id === activeViewId);
        if (v && v.userId === memberId) {
          await DashboardViewService.save({ ...v, config: { ...(v.config || {}), layout: next } });
          await reloadDashboardViews();
          return;
        }
      }
      const allMembers = await TeamService.list();
      const me = allMembers.find((m: any) => m.id === memberId);
      if (!me) return;
      const layouts = { ...(me as any).dashboardLayouts, project_detail: next };
      await TeamService.save({ ...me, dashboardLayouts: layouts } as any);
    } catch (err) {
      console.error('[dashboard layout save]', err);
    }
  };

  const switchToView = (viewId: string | null) => {
    if (viewId === null) {
      setActiveViewId(null);
      (async () => setDashboardLayoutState(await resolveFallbackLayout()))();
      return;
    }
    const v = dashboardViews.find((x) => x.id === viewId);
    if (!v) return;
    const layout = (v.config as any)?.layout;
    if (Array.isArray(layout)) {
      setDashboardLayoutState(layout);
      setActiveViewId(viewId);
    }
  };

  // Set as firm starter (T4) — admin act, gated server-side by
  // admin_center.manage_firm_settings (FirmPrefs.set enforces it).
  const saveFirmStarter = async () => {
    const memberId = authUser?.teamMemberId || authUser?.id;
    if (!memberId) return;
    try {
      await FirmPrefs.set(`dashboard.starter.${DASHBOARD_SURFACE}`, dashboardLayout, memberId);
      toast('Firm starter saved');
    } catch (err: any) {
      toast(err?.message || 'Could not save firm starter', true);
    }
  };

  const saveAsNewView = async () => {
    const userId = authUser?.teamMemberId || authUser?.id;
    if (!userId || !saveViewName.trim()) return;
    const view: DashboardView = {
      id: '',
      userId,
      surface: DASHBOARD_SURFACE,
      name: saveViewName.trim(),
      config: { layout: dashboardLayout },
      shared: saveViewShared,
      isDefault: false,
    };
    const saved = await DashboardViewService.save(view);
    setActiveViewId(saved.id);
    setShowSaveViewDialog(false);
    setSaveViewName('');
    setSaveViewShared(false);
    await reloadDashboardViews();
    toast(`View "${saved.name}" saved.`);
  };

  const deleteActiveView = async () => {
    if (!activeViewId) return;
    const v = dashboardViews.find((x) => x.id === activeViewId);
    if (!v) return;
    if (!confirm(`Delete saved view "${v.name}"?`)) return;
    await DashboardViewService.delete(activeViewId);
    setActiveViewId(null);
    await reloadDashboardViews();
    toast(`View "${v.name}" deleted.`);
  };

  const setActiveAsDefault = async () => {
    const userId = authUser?.teamMemberId || authUser?.id;
    if (!userId || !activeViewId) return;
    await DashboardViewService.setDefault(activeViewId, DASHBOARD_SURFACE, userId);
    await reloadDashboardViews();
    toast('Default view updated.');
  };

  // Snapshot / Export — the user's system print → "Save as PDF" handles
  // the file output; a print stylesheet (index.css) scopes to the grid id.
  const exportDashboardPdf = () => {
    const grid = document.getElementById('project-dashboard-grid');
    if (!grid) return;
    window.print();
  };

  const ctx: ProjectWidgetCtx = {
    project,
    authUser,
    userPermissions,
    can,
    locked,
    members,
    billingRoles,
    teams,
    teamAssignments,
    departments,
    orgs,
    projectTypes,
    billingTypes,
    firmPhases,
    allScopes,
    assignments,
    phases,
    deliverables,
    scopeAssignments,
    projectSpecs,
    formas,
    designBrief,
    designBriefFields,
    saveProject,
    saveDeliverable,
    deleteDeliverable,
    setCurrentPhase,
    savePhase,
    deletePhase,
    reorderPhases,
    assignMember,
    assignTeam,
    setAssignmentRole,
    removeAssignment,
    saveDesignBrief,
    saveDesignBriefField,
    removeDesignBriefField,
    reorderDesignBriefFields,
    onDrill: handleDrill,
    navigate: wrappedNavigate,
    onOpenProjectSpec,
    onOpenForma,
    openTakeover: openWidgetTakeover,
    closeTakeover: closeTakeoverState,
    toast,
    isWeb,
  };

  const currentPhase = project.currentPhaseId
    ? phases.data.find((p) => p.id === project.currentPhaseId)
    : undefined;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      {/* Header bar */}
      <div className="flex flex-shrink-0 items-center gap-3 bg-surface px-5 py-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => (takeover ? closeTakeoverState() : onBack())}
          aria-label="Back to projects"
        >
          <Icon icon={ChevronLeft} em={1} /> Projects
        </Button>
        <div className="flex flex-1 flex-wrap items-center gap-3">
          <span style={{ fontSize: 'var(--font-size-xl)', fontWeight: 600, color: 'var(--tx)' }}>
            {project.projectNumber
              ? `${project.projectNumber} – ${sanitizeProjectName(project.name) || 'Untitled'}`
              : sanitizeProjectName(project.name) || 'Untitled'}
          </span>
          {currentPhase && (
            <span className="rounded-full border border-border px-2 py-0.5 text-xs font-semibold text-muted">
              {currentPhase.name}
            </span>
          )}
          {project.status && !locked && <StatusBadge status={project.status} />}
          {locked && (
            <span
              className="inline-flex items-center gap-1 rounded-full border border-border px-2 py-0.5 text-xs font-semibold text-muted"
              title="Closed — every field, phase, deliverable, team assignment, spec, budget, time entry, forecast and invoice on this project is read-only"
            >
              <Icon icon={Lock} size="xs" aria-hidden="true" />
              Closed
              {project.closedAt &&
                ` · ${new Date(project.closedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`}
              {project.closedBy &&
                members.find((m) => m.id === project.closedBy) &&
                ` · ${members.find((m) => m.id === project.closedBy)!.name}`}
            </span>
          )}
        </div>
        {!takeover && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={exportDashboardPdf}
              title="Print or export this dashboard view as PDF"
            >
              <Icon icon={Download} size="sm" /> Export
            </Button>
            {can.archiveProject && (
              <ContextMenu>
                <ContextMenuTrigger asChild>
                  <Button variant="outline" size="icon" aria-label="Project actions">
                    <Icon icon={MoreHorizontal} size="sm" />
                  </Button>
                </ContextMenuTrigger>
                <ContextMenuContent
                  align="end"
                  sideOffset={4}
                  className="min-w-[10rem] rounded-md border border-border bg-surface p-1 shadow-md"
                >
                  {locked ? (
                    <ContextMenuItem
                      className="cursor-pointer rounded-sm px-2 py-1.5 text-sm outline-none hover:bg-hover"
                      onSelect={() => setConfirmArchive('reopen')}
                    >
                      Reopen project
                    </ContextMenuItem>
                  ) : (
                    <ContextMenuItem
                      className="cursor-pointer rounded-sm px-2 py-1.5 text-sm text-danger outline-none hover:bg-hover"
                      onSelect={() => setConfirmArchive('close')}
                    >
                      Close project
                    </ContextMenuItem>
                  )}
                </ContextMenuContent>
              </ContextMenu>
            )}
          </div>
        )}
      </div>

      {/* Header row 2 (Project Canvas R1, Decisions 3-4) — the views bar is
          now a PERMANENT second header row, never part of the scroller; in
          a takeover it is replaced by the takeover's own title row. Neither
          row ever scrolls — only `#project-dashboard-grid` below does. */}
      {takeover ? (
        <div className="flex flex-shrink-0 items-center gap-2 border-b border-border bg-surface px-5 py-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={closeTakeoverState}
            aria-label="Back to dashboard"
          >
            <Icon icon={ChevronLeft} em={1} /> Back to dashboard
          </Button>
          {/* Widget-def takeovers (Specs, Deliverables) keep today's title
              row; nested REAL pages (Decision 6/C) render their own
              heading, so this row stays slim — no duplicate title. */}
          {takeover.kind === 'widget' && (
            <span className="text-sm font-semibold text-text">
              {activeWidgetTakeoverDef?.takeoverTitle ?? activeWidgetTakeoverDef?.title ?? ''}
            </span>
          )}
        </div>
      ) : (
        <div className="flex-shrink-0 border-b border-border bg-surface px-5 py-2">
          <DashboardViewsBar
            views={dashboardViews}
            activeViewId={activeViewId}
            currentUserId={authUser?.teamMemberId || authUser?.id || ''}
            onSwitch={switchToView}
            onSaveAs={() => setShowSaveViewDialog(true)}
            onSetDefault={setActiveAsDefault}
            onDelete={deleteActiveView}
            canSetFirmStarter={hasPermission(userPermissions, 'admin_center.manage_firm_settings')}
            onSetFirmStarter={saveFirmStarter}
            customizing={customizing}
            onToggleCustomize={() => setCustomizing((c) => !c)}
          />
        </div>
      )}

      {takeover ? (
        <div
          className={
            takeover.kind === 'tool'
              ? 'min-h-0 flex-1 overflow-hidden'
              : 'min-h-0 flex-1 overflow-auto'
          }
        >
          {takeover.kind === 'widget' ? (
            activeWidgetTakeoverDef?.takeover ? (
              activeWidgetTakeoverDef.takeover(ctx)
            ) : null
          ) : (
            <ProjectToolHost
              tool={takeover.tool}
              projectId={project.id}
              deepLink={takeover.deepLink ?? null}
              authUser={authUser}
              toast={toast}
            />
          )}
        </div>
      ) : (
        <div id="project-dashboard-grid" className="min-h-0 flex-1 overflow-auto p-4">
          {/* Project Canvas R1 (T2) — Details/Phase/Design brief are stock
              widgets now (`project_details`/`project_phase`/`project_brief`),
              not page chrome; `#project-dashboard-grid` holds ONLY the grid. */}
          <WidgetGrid<ProjectWidgetCtx>
            widgets={visibleWidgets(allProjectWidgets, userPermissions)}
            // ensureRequiredWidgets backfills any `required` def missing from
            // a saved view/legacy blob (predates that widget). This is a
            // render-time patch only — it persists naturally the next time
            // the user drags/resizes through onLayoutChange, never written
            // back here (would risk a save loop on every render).
            layout={ensureRequiredWidgets(
              migrateDashboardLayout(dashboardLayout, sectionOrder),
              visibleWidgets(allProjectWidgets, userPermissions)
            )}
            defaultLayout={defaultLayoutFor(allProjectWidgets, userPermissions)}
            userPermissions={userPermissions}
            ctx={ctx}
            onLayoutChange={saveDashboardLayout}
            customizing={customizing}
            currentUserId={authUser?.teamMemberId || authUser?.id}
            onEditCustomWidget={editCustomWidgetById}
            onDuplicateCustomWidget={duplicateCustomWidgetById}
            onRenameCustomWidget={renameCustomWidgetById}
            onDeleteCustomWidget={deleteCustomWidgetById}
            canManageCustomWidget={canManageCustomWidget}
            onNewWidget={
              locked
                ? undefined
                : () => {
                    setEditingWidget(null);
                    setBuilderOpen(true);
                  }
            }
          />
        </div>
      )}

      <Dialog open={showSaveViewDialog} onOpenChange={setShowSaveViewDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save current layout as a view</DialogTitle>
            <DialogDescription>
              Views can be switched back to any time and, if shared, seen by the whole firm.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="dashboard-view-name">View name</Label>
              <Input
                id="dashboard-view-name"
                autoFocus
                placeholder="e.g. PIC view, Accounting"
                value={saveViewName}
                onChange={(e) => setSaveViewName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') saveAsNewView();
                }}
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-muted">
              <Checkbox
                checked={saveViewShared}
                onCheckedChange={(v) => setSaveViewShared(v === true)}
              />
              Share with the firm (everyone sees this view)
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => setShowSaveViewDialog(false)}>
              Cancel
            </Button>
            <Button size="sm" onClick={saveAsNewView} disabled={!saveViewName.trim()}>
              Save view
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CustomWidgetBuilder
        open={builderOpen}
        onClose={() => {
          setBuilderOpen(false);
          setEditingWidget(null);
        }}
        ctx={ctx}
        editing={editingWidget}
        surface={DASHBOARD_SURFACE}
        onSaved={() => {
          setBuilderOpen(false);
          setEditingWidget(null);
          reloadCustomWidgets();
        }}
      />

      <ConfirmDialog
        open={!!deletingWidget}
        onOpenChange={(o) => !o && setDeletingWidget(null)}
        title="Delete widget?"
        description={`"${deletingWidget?.name ?? ''}" will be removed from every dashboard it's shared to.`}
        confirmLabel="Delete"
        busy={deletingWidgetBusy}
        onConfirm={deleteCustomWidget}
      />

      <ConfirmDialog
        open={confirmArchive === 'close'}
        onOpenChange={(o) => !o && !archiveBusy && setConfirmArchive(null)}
        title="Close this project?"
        description="Every field, phase, deliverable, team assignment, spec, budget, time entry, forecast and invoice on this project becomes read-only. Only Reopen can change it."
        confirmLabel="Close project"
        destructive
        busy={archiveBusy}
        onConfirm={() => void closeOrReopenProject('close')}
      />
      <ConfirmDialog
        open={confirmArchive === 'reopen'}
        onOpenChange={(o) => !o && !archiveBusy && setConfirmArchive(null)}
        title="Reopen this project?"
        description="Every field, phase, deliverable, team assignment, spec, budget, time entry, forecast and invoice becomes editable again."
        confirmLabel="Reopen project"
        destructive={false}
        busy={archiveBusy}
        onConfirm={() => void closeOrReopenProject('reopen')}
      />
    </div>
  );
}
